import { ReactNode, useContext, useEffect, useState } from 'react';
import { createContext } from 'react';

import {
  PORTFOLIO_CONTRACTS,
  RISK_METRIC_LIST,
  RISK_TOKEN_LIST,
  TIME_LIST,
  TOKEN_LIST,
  VENUE_TYPE_LIST,
  VOL_SHOCK_LIST,
  VOL_SHOCK_REAL_RISK,
} from '../../constants/reports/reports-filters';
import { PortfolioManagementContext } from '../portfolio-management/portfolio-management-provider';
import usePersistentState from '../use-persistent-state';

export enum riskTypes {
  spotLadder = 'SPOT_LADDER',
  spotGrid = 'SPOT_GRID',
  spotTime = 'SPOT_TIME',
}

export type OrderItemType = {
  id: string;
  label?: string;
};

export type orderRowsListType = {
  [key in riskTypes]: OrderItemType[];
};

interface IRiskFiltersParamsProvider {
  children: ReactNode;
}

interface IRiskFiltersProvider {
  filters: any;
}

const DEFAULT_ORDER_ROW_LIST: orderRowsListType = {
  [riskTypes.spotLadder]: [],
  [riskTypes.spotGrid]: [],
  [riskTypes.spotTime]: [],
};

export const RiskFiltersContext = createContext<IRiskFiltersProvider>({} as IRiskFiltersProvider);

export const RiskFiltersProvider = ({ children }: IRiskFiltersParamsProvider) => {
  const STATE_PATH = ['RISK_FILTERS'];

  const { selectedAccountOption, selectedDataDisplayOption } = useContext(
    PortfolioManagementContext,
  );

  const AGGREGATED_KEY = 'aggregated';
  const { RISK } = PORTFOLIO_CONTRACTS;

  const [slider, setSlider] = usePersistentState(STATE_PATH, 'sliderRiskManagement', [-5, 150]);
  const [token, setToken] = usePersistentState<string>(STATE_PATH, 'token', TOKEN_LIST[0].value);
  const [tokenList, setTokenList] = usePersistentState(STATE_PATH, 'tokenList', TOKEN_LIST);
  const [riskMetricList, setRiskMetricList] = useState(RISK_METRIC_LIST);
  const [riskMetric, setRiskMetric] = usePersistentState<string>(
    STATE_PATH,
    'riskMetricParam',
    RISK_METRIC_LIST[0].value,
  );
  const [venueType, setVenueType] = usePersistentState(
    STATE_PATH,
    'venueType',
    VENUE_TYPE_LIST[0].value,
  );
  const [riskToken, setRiskToken] = usePersistentState<string>(
    STATE_PATH,
    'riskToken',
    RISK_TOKEN_LIST[0].value,
  );
  const [volShock, setVolShock] = usePersistentState<string>(
    STATE_PATH,
    'volShock',
    VOL_SHOCK_REAL_RISK.value,
  );
  const [time, setTime] = usePersistentState<string>(STATE_PATH, 'time', TIME_LIST[0].value);
  const [riskInstType, setRiskInstType] = useState(RISK.CONTRACT_VALUES);
  const [expandableRows, setExpandableRows] = usePersistentState(STATE_PATH, 'expandableRows', []);
  const [expandableColums, setExpandableColums] = usePersistentState(
    STATE_PATH,
    'expandableColums',
    [],
  );
  const [orderRowsList, setOrderRowsList] = usePersistentState<orderRowsListType>(
    STATE_PATH,
    'orderRowsList',
    DEFAULT_ORDER_ROW_LIST,
  );
  const [defaultOrderRowsList, setDefaultOrderRowsList] =
    useState<orderRowsListType>(DEFAULT_ORDER_ROW_LIST);
  const [volShockList, setVolShockList] = usePersistentState(
    STATE_PATH,
    'volShockArrList',
    VOL_SHOCK_LIST,
  );
  const [timeList, setTimeList] = usePersistentState(STATE_PATH, 'timeList', TIME_LIST);
  const [error, setError] = usePersistentState(STATE_PATH, 'error', false);

  const [accountRiskSettings, setAccountRiskSettings] = usePersistentState<Record<string, any>>(
    STATE_PATH,
    'accountRiskSettings',
    {},
  );

  const updateAccountRiskSettings = (riskToken: string) => {
    if (selectedDataDisplayOption === 'aggregated') {
      setAccountRiskSettings((prev) => ({
        ...prev,
        [AGGREGATED_KEY]: {
          ...prev[AGGREGATED_KEY],
          riskToken,
        },
      }));
    } else if (selectedAccountOption?.name) {
      setAccountRiskSettings((prev) => ({
        ...prev,
        [selectedAccountOption.name]: {
          ...prev[selectedAccountOption.name],
          riskToken,
        },
      }));
    }
  };

  useEffect(() => {
    if (selectedAccountOption?.name && accountRiskSettings[selectedAccountOption.name]) {
      setRiskToken(accountRiskSettings[selectedAccountOption.name].riskToken);
    } else {
      updateAccountRiskSettings('QUOTE');
    }
  }, [selectedAccountOption]);

  useEffect(() => {
    if (selectedDataDisplayOption === 'aggregated') {
      if (accountRiskSettings[AGGREGATED_KEY]) {
        setRiskToken(accountRiskSettings[AGGREGATED_KEY].riskToken);
      }
    } else if (selectedAccountOption?.name && accountRiskSettings[selectedAccountOption.name]) {
      setRiskToken(accountRiskSettings[selectedAccountOption.name].riskToken);
    }
  }, [selectedDataDisplayOption]);

  useEffect(() => {
    updateAccountRiskSettings(riskToken);
  }, [riskToken]);

  const filters = {
    slider,
    setSlider,
    token,
    setToken,
    tokenList,
    setTokenList,
    riskMetric,
    setRiskMetric,
    venueType,
    setVenueType,
    riskToken,
    setRiskToken,
    volShock,
    setVolShock,
    volShockList,
    setVolShockList,
    time,
    setTime,
    timeList,
    setTimeList,
    riskInstType,
    setRiskInstType,
    error,
    setError,
    expandableRows,
    expandableColums,
    setExpandableRows,
    setExpandableColums,
    orderRowsList,
    setOrderRowsList,
    riskMetricList,
    setRiskMetricList,
    accountRiskSettings,
    updateAccountRiskSettings,
    defaultOrderRowsList,
    setDefaultOrderRowsList,
  };

  return (
    <RiskFiltersContext.Provider
      value={{
        filters,
      }}
    >
      {children}
    </RiskFiltersContext.Provider>
  );
};
